import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from 'api/api';
import TokenUtils from 'utils/TokenUtils';
import { addNotification } from './notificationsSlice';

export const getTransactions = createAsyncThunk('transactions/getTransactions', async (params, { rejectWithValue }) => {
  try {
    const response = await api.get('/transactions', { params });
    return { ...response.data, isLoadMore: params.page > 1 };
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати транзакції');
  }
});

export const loadMoreTransactions = createAsyncThunk(
  'transactions/loadMoreTransactions',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.get('/transactions', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Не вдалося отримати транзакції');
    }
  }
);

export const createTransaction = createAsyncThunk(
  'transactions/createTransaction',
  async (transactionData, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.post('/transactions', transactionData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Транзакцію успішно створено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка створення транзакції');
    }
  }
);

export const createTransactionSilent = async (transactionData) => {
  try {
    const token = TokenUtils.getToken();
    const response = await api.post('/transactions', transactionData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Помилка створення транзакції');
  }
};

export const updateTransaction = createAsyncThunk(
  'transactions/updateTransaction',
  async ({ id, ...updateData }, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      const response = await api.put(`/transactions/${id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Транзакцію успішно оновлено',
          type: 'success',
        })
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка оновлення транзакції');
    }
  }
);

export const deleteTransaction = createAsyncThunk(
  'transactions/deleteTransaction',
  async (transactionID, { dispatch, rejectWithValue }) => {
    try {
      const token = TokenUtils.getToken();
      await api.delete(`/transactions/${transactionID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      dispatch(
        addNotification({
          id: Date.now(),
          title: 'Транзакцію видалено',
          type: 'success',
        })
      );

      return transactionID;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Помилка видалення транзакції');
    }
  }
);

const transactionsSlice = createSlice({
  name: 'transactions',
  initialState: {
    transactions: [],
    isLoading: false,
    isLoadingMore: false,
    error: null,
    meta: {
      total: 0,
      page: 1,
      limit: 30,
      totalPages: 0,
      hasMore: false,
    },
    currentParams: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getTransactions.pending, (state, action) => {
        const isLoadMore = action.meta.arg.page > 1;
        if (isLoadMore) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
          state.transactions = [];
        }
        state.error = null;
      })
      .addCase(getTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;

        const { data, meta, isLoadMore } = action.payload;

        if (isLoadMore) {
          state.transactions = [...state.transactions, ...data];
        } else {
          state.transactions = data;
        }

        state.meta = meta;
        state.currentParams = action.meta.arg;
      })
      .addCase(getTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload;
      })

      .addCase(loadMoreTransactions.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreTransactions.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        const { data, meta } = action.payload;

        state.transactions = [...state.transactions, ...data];
        state.meta = meta;
      })
      .addCase(loadMoreTransactions.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload;
      })

      .addCase(createTransaction.fulfilled, (state, action) => {
        state.transactions.push(action.payload);
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(updateTransaction.fulfilled, (state, action) => {
        const index = state.transactions.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.transactions[index] = action.payload;
        }
      })
      .addCase(updateTransaction.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addCase(deleteTransaction.fulfilled, (state, action) => {
        state.transactions = state.transactions.filter((transaction) => action.payload !== transaction.id);
      })
      .addCase(deleteTransaction.rejected, (state, action) => {
        state.error = action.payload;
      })

      .addMatcher(
        (action) =>
          (action.type.startsWith('transactions/createTransaction') ||
            action.type.startsWith('transactions/updateTransaction') ||
            action.type.startsWith('transactions/deleteTransaction')) &&
          action.type.endsWith('/pending'),
        (state) => {
          state.error = null;
        }
      );
  },
});

export default transactionsSlice.reducer;
